import React, { useRef, useMemo } from 'react';
import { theme } from '../../../theme';
import type { ChatbotAnalytics } from '../../../services/chatbotApi';
import { useEChartsLifecycle, INVISIBLE_TOOLTIP_OPTS } from './_chartCommon';
import { useCategoryMeta } from '../../../context/ClassificationSchemaContext';

// <purpose>Konversationsaufkommen KW-Balken — weekly conversation volume bars with per-K
// category hover breakdown. Mirrors KwBalken exactly: one silent solid total-bar series (z:1)
// + one silent stacked bar series per category key, overlaid via barGap:'-100%'. Hover-morph:
// solid fades on emphasis, K-stack reveals per category. Data is simpler than KwBalken — the
// input byPrimaryAxis map is already per-bin, no row-grouping step needed.</purpose>
// <dependencies>useEChartsLifecycle, useCategoryMeta, ChatbotAnalytics['konversationsaufkommen']</dependencies>
// <constraints>
//   All series silent:true — native emphasis disabled; only the updateAxisPointer dispatch loop
//   drives the morph reveal. First K-series uses barGap:'-100%' to overlay on the solid bar.
//   Renders a 'Keine Daten' fallback when data is empty (jsdom guard + UX).
// </constraints>

type KonvBin = ChatbotAnalytics['konversationsaufkommen'][number];

type Props = {
  data: KonvBin[];
  filterHash?: string;
};

/** The stack's key list, taken from what the SERVER sent rather than predicted from the schema.
 *
 * Exported and pure so it is testable at all: the series only exist inside an ECharts `setOption`
 * payload, which jsdom cannot see — a DOM-level test of this property would assert nothing.
 *
 * Vocabulary order is preserved so colours stay stable across renders; keys the vocabulary does not
 * know are appended (sorted, for determinism) rather than dropped, because a category the backend
 * sent is real whether or not the tenant's schema has heard of it. */
export const resolveStackKeys = (bins: KonvBin[], categoryKeys: string[]): string[] => {
  const sent = new Set<string>();
  for (const bin of bins) for (const k of Object.keys(bin?.byPrimaryAxis ?? {})) sent.add(k);
  const known = categoryKeys.filter(k => sent.has(k));
  const unknown = [...sent].filter(k => !categoryKeys.includes(k)).sort();
  return [...known, ...unknown];
};

export const KonversationsaufkommenChart: React.FC<Props> = ({ data, filterHash }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cm = useCategoryMeta();
  // A stacked VOLUME chart is a volume surface: it reconciles against its bar total, unclassified bucket included
  // (see KwBalken's cm.categoryKeys). The performance-surface rule — drop the unclassified bucket — governs RATE
  // computations, not volume stacks, so stacking alone does not put this chart under that rule.
  //
  // And this series inherits neither rule by proximity: byPrimaryAxis is CHATBOT data
  // (ChatbotAnalytics['konversationsaufkommen']), not the ticket-classification set that
  // categoryKeys/performanceCategoryKeys describe. The chatbot backend excludes "nicht verstanden"
  // (KU) upstream through its own SQL filter, with its own total = sum(byPrimaryAxis) and KU tracked
  // separately via unclassifiedRate (mirrors chatbotApi.ts).
  //
  // ⚠ So the stack keys are taken from the DATA, never predicted from the schema. This used to read
  // `cm.performanceCategoryKeys`, which agreed with the chatbot's exclusion only by coincidence —
  // one excluded id on each side — and would have silently stopped matching the moment a tenant
  // vocabulary marked a second value `excludedFromEvaluation`: a category the server still sent,
  // missing from the stack, with the bar total unchanged and nothing to notice. Reading the union of
  // byPrimaryAxis keys instead removes the class rather than pinning the coincidence; the two sets are
  // now free to diverge and this chart still renders what it was handed. Vocabulary order is kept
  // for stable colours, and keys the vocabulary does not know are appended rather than dropped.
  const stackKeys = useMemo(() => resolveStackKeys(data, cm.categoryKeys), [data, cm.categoryKeys]);

  useEChartsLifecycle(containerRef, chart => {
    const labels = data.map(d => d.binLabel);
    const values = data.map(d => d.total);

    // byPrimaryAxis is already a per-bin map — no kPerBin build step needed
    const kSeriesData = stackKeys.reduce<Record<string, number[]>>((acc, k) => {
      acc[k] = data.map(d => d.byPrimaryAxis[k] ?? 0);
      return acc;
    }, {});

    chart.setOption({
      grid: { left: 36, right: 16, top: 38, bottom: 26 },
      tooltip: INVISIBLE_TOOLTIP_OPTS,
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: '#dde0e8' } },
        axisTick: { show: false },
        axisLabel: { color: '#9095a3', fontSize: 9, interval: data.length > 16 ? 1 : 0 },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#f1f2f6' } },
        axisLabel: { color: '#9095a3', fontSize: 9 },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          name: 'solid',
          type: 'bar',
          silent: true,
          data: values,
          barWidth: '60%',
          itemStyle: {
            color: theme.colors.navyMid,
            borderRadius: [4, 4, 0, 0],
            shadowColor: 'rgba(6,16,90,0.16)',
            shadowBlur: 4,
            shadowOffsetY: 2,
          },
          emphasis: { itemStyle: { opacity: 0 } },
          label: {
            show: true,
            position: 'top',
            color: theme.colors.navy,
            fontSize: 10,
            fontWeight: 600,
            formatter: (p: any) => String(values[p.dataIndex] ?? ''),
          },
          z: 1,
        },
        ...stackKeys.map((k, i) => ({
          name: k,
          type: 'bar' as const,
          silent: true,
          stack: 'k',
          ...(i === 0 ? { barGap: '-100%' } : {}),
          barWidth: '60%',
          data: kSeriesData[k],
          itemStyle: {
            color: cm.color(k),
            opacity: 0,
          },
          label: { show: false },
          emphasis: { itemStyle: { opacity: 0.92 } },
          z: 2,
        })),
      ],
    });

    // Hover-morph: axisPointer dispatch loop drives highlight/downplay across all series
    let lastIdx = -1;
    const SERIES_COUNT = 1 + stackKeys.length;
    chart.on('updateAxisPointer', (event: any) => {
      const xa = event.axesInfo?.[0];
      const idx = (xa && xa.value !== undefined && xa.value >= 0) ? xa.value : -1;
      if (idx === lastIdx) return;
      if (lastIdx >= 0) {
        for (let s = 0; s < SERIES_COUNT; s++) {
          chart.dispatchAction({ type: 'downplay', seriesIndex: s, dataIndex: lastIdx });
        }
      }
      if (idx >= 0 && idx < values.length) {
        for (let s = 0; s < SERIES_COUNT; s++) {
          chart.dispatchAction({ type: 'highlight', seriesIndex: s, dataIndex: idx });
        }
      }
      lastIdx = idx;
    });
  }, [data, filterHash, stackKeys, cm.categoryLabelsFromData]);

  if (data.length === 0) {
    return (
      <div data-stx-konv-chart style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220, color: '#9095a3', fontSize: 13 }}>
        Keine Daten
      </div>
    );
  }

  return <div data-stx-konv-chart ref={containerRef} style={{ width: '100%', height: 220 }} />;
};
